/* Third Party */
import { Route, Routes } from "react-router-dom";
import { useRef, useEffect } from "react";
import { useDispatch, Provider } from "react-redux";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";

/* Common Components */
import Header from "components/common/header";
import Footer from "components/common/footer";
import NavigationBar from "components/common/navigationBar";
import { ProtectedRoute } from "components/common/protectedRoute";

/* Pages */
import OfficeMonitoring from "pages/officeMonitoring";
import Timelog from "pages/timelog";
import SelfHelpDesk from "pages/selfHelpDesk";
import CubeDEX from "pages/cubedex";
import TimelogReport from "pages/timelogReport";
import {
  Unauthorized,
  BadRequest,
  Forbidden,
  NotFound,
  InternalServerError,
} from "pages/errors";

/* CSS */
import "assets/css/tabler.min.css";
import "assets/css/tabler-flags.min.css";
import "assets/css/tabler-payments.min.css";
import "assets/css/tabler-vendors.min.css";
import "assets/css/demo.min.css";

/* JS */
import "assets/js/demo.min.js";
import "assets/js/tabler.min.js";

/* Config */
import { loginRequest } from "config/authentication/azure/authConfig";
/* API */
import { getEmployee } from "pages/officeMonitoring/services/api";
import { setAccessToken } from "pages/officeMonitoring/services/api/client";
import { setAccessToken as setCubeDEXAccessToken } from "pages/cubedex/services/api/client";
/* Store */
import { setUserInfo, setUserLoading, setUserError } from "store/userSlice";
import { store as officeMonitoringStore } from "pages/officeMonitoring/state/stores";
import { store as selfHelpDeskStore } from "pages/selfHelpDesk/state/stores";

const ForceLogin = () => {
  const { instance } = useMsal();
  instance.loginRedirect(loginRequest).catch((e) => {
    console.log(e);
  });
  return <></>;
};

const Default = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const dispatch = useDispatch();
  const hasFetchedRef = useRef(false);


  useEffect(() => {

    // Wait for MSAL to finish any in-progress operations
    if (inProgress !== "none") {
      console.log("MSAL operation in progress:", inProgress);
      return;
    }

    if (!accounts || accounts.length === 0) {
      console.log("No accounts available");
      return;
    }

    if (hasFetchedRef.current) {
      console.log("Already fetched, skipping");
      return;
    }

    hasFetchedRef.current = true;

    const fetchEmployeeInfo = async () => {
      try {
        dispatch(setUserLoading(true));

        const { accessToken } = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });

        setAccessToken(accessToken);
        setCubeDEXAccessToken(accessToken);

        const userEmail = accounts[0].username;
        const response = await getEmployee(userEmail);


        // Save to global Redux store
        dispatch(setUserInfo({
          ...response.data,
        }));
      } catch (error) {
        console.error("ERROR in fetchEmployeeInfo:", error);
        dispatch(setUserError(error.message));
        hasFetchedRef.current = false;
      }
    };

    fetchEmployeeInfo();
  });

  // Empty dependency so it runs on every render until hasFetchedRef becomes true

  return (
    <>
      <AuthenticatedTemplate>
        <div className="layout-default">
          <div className="wrapper">
            <Header />
            <NavigationBar />
            <div className="page-wrapper">
              <Routes>
                 <Route
                  path="/"
                  element={
                    <Provider store={officeMonitoringStore}>
                      <OfficeMonitoring />
                    </Provider>
                  }
                />
                <Route
                  path="/selfhelpdesk"
                  element={
                    <Provider store={selfHelpDeskStore}>
                      <SelfHelpDesk />
                    </Provider>
                  }
                />
                <Route
                  path="/cubedex"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <CubeDEX />
                    </ProtectedRoute>
                  }
                />
                <Route path="/admin-report" element={<TimelogReport />} />
                <Route path="/400" element={<BadRequest />} />
                <Route path="/401" element={<Unauthorized />} />
                <Route path="/403" element={<Forbidden />} />
                <Route path="/404" element={<NotFound />} />
                <Route path="/500" element={<InternalServerError />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
            <Footer />
          </div>
        </div>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <ForceLogin />
      </UnauthenticatedTemplate>
    </>
  );
};
export default Default;
